export function compareFit(sizes, usual, comfort) {
  const exact = sizes.find(s => s.number === usual);
  const candidates = comfort > 0 ? sizes.filter(s => s.number > usual && s.number <= usual + 1) : comfort < 0 ? sizes.filter(s => s.number < usual && s.number >= usual - 1) : [];
  const match = comfort > 0 ? candidates[0] : comfort < 0 ? candidates.at(-1) : exact;
  const reference = match?.number ?? usual + comfort;
  const smaller = sizes.filter(s => s.number < reference).at(-1);
  const larger = sizes.find(s => s.number > reference);
  return {match, reference, nearby:[smaller,match,larger].filter(Boolean)};
}

if (!customElements.get('golden-fit-studio')) {
  class GoldenFitStudio extends HTMLElement {
    static answers = new Map();
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const signal = this.controller.signal;
      this.copy = JSON.parse(this.querySelector('[data-fit-copy]').textContent);
      this.form = this.querySelector('[data-fit-form]');
      this.input = this.querySelector('[data-fit-size]');
      this.details = this.querySelector('details');
      this.apply = this.querySelector('[data-fit-apply]');
      this.rememberBox = this.querySelector('[data-fit-remember]');
      this.sizes = Array.from(this.querySelector('[data-fit-options]').options).map(o => ({label:o.value,number:Number(o.value.replace(',','.')),id:o.dataset.optionValueId,available:o.dataset.available === 'true'})).filter(s => Number.isFinite(s.number) && s.number >= 18 && s.number <= 50).sort((a,b) => a.number-b.number);
      this.step = 0;
      this.choiceNumber = null;
      let previous = GoldenFitStudio.answers.get(this.dataset.productId);
      if (!previous) {
        try {
          const saved = JSON.parse(sessionStorage.getItem('golden-fit-preferences'));
          if (saved && Number(saved.size)>=18 && Number(saved.size)<=50) previous = {...saved,step:0,open:false,remember:true};
        } catch {}
      }
      if (previous) {
        this.input.value = previous.size;
        for (const [kind,value] of Object.entries({comfort:previous.comfort,width:previous.width,use:previous.use})) {
          const radio = this.form.querySelector('input[name^="fit-'+kind+'-"][value="'+value+'"]');
          if (radio) radio.checked = true;
        }
        this.rememberBox.checked = !!previous.remember;
        this.details.open = !!previous.open;
        this.choiceNumber = previous.choiceNumber ?? null;
        this.step = previous.step ?? 0;
        if (this.step === 2 && this.input.checkValidity()) this.compare(false);
      }
      this.updateFoot();
      this.showStep(this.step,false);
      this.querySelectorAll('[data-fit-adjust]').forEach(button => button.addEventListener('click',() => {
        const current = this.input.value === '' ? 38 : Number(this.input.value);
        this.input.value = String(Math.max(18,Math.min(50,current+Number(button.dataset.fitAdjust))));
        this.choiceNumber = null;
        this.remember();
      },{signal}));
      this.querySelector('[data-fit-next]').addEventListener('click',() => {if(this.validSize())this.showStep(1);},{signal});
      this.form.addEventListener('submit',event => {event.preventDefault();if(this.validSize())this.compare();},{signal});
      this.querySelectorAll('[data-fit-go]').forEach(button => button.addEventListener('click',() => {
        const step = Number(button.dataset.fitGo);
        if (step === 0) this.showStep(0);
        else if (this.validSize()) { if(step === 2)this.compare();else this.showStep(1); }
      },{signal}));
      this.querySelector('[data-fit-back]').addEventListener('click',() => this.showStep(0),{signal});
      this.querySelector('[data-fit-edit]').addEventListener('click',() => this.showStep(0),{signal});
      this.form.addEventListener('input',() => {this.choiceNumber=null;this.updateFoot();this.remember();},{signal});
      this.form.addEventListener('change',() => this.remember(),{signal});
      this.details.addEventListener('toggle',() => this.remember(),{signal});
      this.apply.addEventListener('click',() => this.selectSize(),{signal});
      this.querySelector('[data-fit-sizes]').addEventListener('click',event => {
        const button = event.target.closest('[data-fit-choice]');
        if (!button || button.disabled) return;
        this.target = this.sizes.find(s=>s.id === button.dataset.fitChoice);
        this.choiceNumber = this.target?.number ?? null;
        this.updateChoice();
        this.remember();
      },{signal});
    }
    disconnectedCallback() { this.controller?.abort(); }
    values() {
      return {size:this.input.value,comfort:this.form.querySelector('input[name^="fit-comfort-"]:checked')?.value ?? '0',width:this.form.querySelector('[data-fit-width]:checked')?.value ?? 'regular',use:this.form.querySelector('[data-fit-use]:checked')?.value ?? 'daily',remember:this.rememberBox.checked};
    }
    remember() {
      const values = this.values();
      GoldenFitStudio.answers.set(this.dataset.productId,{...values,step:this.step,open:this.details.open,choiceNumber:this.choiceNumber});
      try {
        if(values.remember)sessionStorage.setItem('golden-fit-preferences',JSON.stringify(values));
        else sessionStorage.removeItem('golden-fit-preferences');
      } catch {}
    }
    validSize() {
      if (this.input.checkValidity()) return true;
      this.showStep(0,false);
      this.input.reportValidity();
      this.input.focus();
      return false;
    }
    showStep(step, focus=true) {
      this.step = step;
      this.querySelectorAll('[data-fit-panel]').forEach(panel=>panel.hidden = Number(panel.dataset.fitPanel)!==step);
      this.querySelectorAll('[data-fit-go]').forEach(button=>{
        if(Number(button.dataset.fitGo)===step)button.setAttribute('aria-current','step');else button.removeAttribute('aria-current');
        button.toggleAttribute('data-complete',Number(button.dataset.fitGo)<step);
      });
      this.querySelector('[data-fit-progress]').style.width=((step+1)/3*100)+'%';
      if(focus)this.querySelector('[data-fit-panel="'+step+'"] .gs-fit__panel-title')?.focus({preventScroll:true});
      this.remember();
    }
    updateFoot() {
      const width = this.values().width;
      this.querySelector('[data-fit-foot]').style.setProperty('--fit-foot-width',width==='wide'?'1.28':width==='slim'?'.82':'1');
      this.querySelector('[data-fit-width-label]').textContent=this.copy[width];
    }
    message(key,size,usual) { return (this.copy[key]??'').replaceAll('__size__',size).replaceAll('__usual__',usual); }
    compare(focus=true) {
      const values = this.values();
      const comfort = Number(values.comfort);
      const fit = compareFit(this.sizes,Number(values.size),comfort);
      this.reference = fit.reference;
      this.target = this.sizes.find(s=>s.number===this.choiceNumber && s.available) ?? fit.match;
      this.querySelector('[data-fit-number]').textContent='EU '+(fit.match?.label??fit.reference);
      this.querySelector('[data-fit-explanation]').textContent=this.message(comfort>0?'tight':comfort<0?'loose':'comfortable',fit.match?.label??fit.reference,values.size);
      const availability = this.querySelector('[data-fit-availability]');
      availability.textContent=this.copy[fit.match?.available?'available':'unavailable'];
      availability.toggleAttribute('data-unavailable',!fit.match?.available);
      this.querySelector('[data-fit-width-tip]').textContent=this.copy[values.width+'_tip'];
      this.querySelector('[data-fit-use-tip]').textContent=this.copy[values.use+'_tip'];
      const choices=this.querySelector('[data-fit-sizes]');
      choices.replaceChildren();
      fit.nearby.forEach(size=>{
        const button=document.createElement('button');button.type='button';button.className='gs-fit__size-card';button.dataset.fitChoice=size.id;button.disabled=!size.available;button.setAttribute('aria-pressed','false');
        const number=document.createElement('strong');number.textContent='EU '+size.label;
        const role=document.createElement('span');role.textContent=this.copy[size.number===fit.reference?'reference':size.number<fit.reference?'smaller':'larger'];
        const stock=document.createElement('span');stock.textContent=this.copy[size.available?'available':'unavailable'];
        button.append(number,role,stock);choices.append(button);
      });
      if(!fit.match)this.querySelector('[data-fit-explanation]').textContent+=' '+this.copy.not_in_range;
      this.updateChoice();
      this.showStep(2,focus);
    }
    updateChoice() {
      this.querySelectorAll('[data-fit-choice]').forEach(b=>b.setAttribute('aria-pressed',String(b.dataset.fitChoice===this.target?.id)));
      this.apply.disabled=!this.target?.available;
      this.apply.textContent=this.target?.available?this.message('select',this.target.label,this.input.value):this.copy.unavailable;
      this.querySelector('[data-fit-chosen]').textContent=this.target?.available?this.message('chosen',this.target.label,this.input.value):this.copy.manual;
    }
    selectSize() {
      if (!this.target?.available) return;
      const container=this.closest('.product-details')??this.closest('.shopify-section');
      const picker=container?.querySelector('variant-picker[data-product-id="'+this.dataset.productId+'"]');
      const choice=Array.from(picker?.querySelectorAll('input[type="radio"]')??[]).find(input=>input.dataset.optionValueId===this.target.id);
      this.choiceNumber=this.target.number;
      this.remember();
      if(choice && !choice.disabled) {
        choice.click();choice.focus({preventScroll:true});
        picker.scrollIntoView({behavior:window.matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth',block:'center'});
        this.apply.textContent=this.message('selected',this.target.label,this.input.value);
        return;
      }
      const option=Array.from(picker?.querySelectorAll('select option')??[]).find(o=>o.dataset.optionValueId===this.target.id);
      if(option && !option.disabled) {
        option.parentElement.value=option.value;option.parentElement.dispatchEvent(new Event('change',{bubbles:true}));option.parentElement.focus({preventScroll:true});
        this.apply.textContent=this.message('selected',this.target.label,this.input.value);
        return;
      }
      this.querySelector('[data-fit-chosen]').textContent=this.copy.manual;
    }
  }
  customElements.define('golden-fit-studio',GoldenFitStudio);
}
